import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import { Card, SectionHeader } from '@/components/Card';
import { contactGroupsService } from '@/services/contact-groups.service';
import { Users } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

// Named so the picker reads as a choice, not eight anonymous blobs.
const PRESET_COLORS: Array<{ hex: string; name: string }> = [
  { hex: '#C8A55B', name: 'Gold' },
  { hex: '#4F7032', name: 'Olive' },
  { hex: '#94372C', name: 'Rust' },
  { hex: '#3A6FA8', name: 'Steel' },
  { hex: '#8A6A1F', name: 'Bronze' },
  { hex: '#6E4E92', name: 'Plum' },
  { hex: '#A8557C', name: 'Berry' },
  { hex: '#2E7D7A', name: 'Teal' },
];

export default function GroupForm() {
  const { uid } = useParams<{ uid: string }>();
  const nav = useNavigate();
  const editing = !!uid;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#C8A55B');
  const [busy, setBusy] = useState(false);
  const [memberCount, setMemberCount] = useState<number | null>(null);

  useEffect(() => {
    if (!editing) return;
    contactGroupsService.get(uid!).then((g) => {
      setName(g.name);
      setDescription(g.description ?? '');
      setColor(g.color);
      setMemberCount(typeof g.contactCount === 'number' ? g.contactCount : null);
    });
  }, [editing, uid]);

  const submit = async () => {
    setBusy(true);
    try {
      if (editing) await contactGroupsService.update(uid!, { name, description, color });
      else await contactGroupsService.create({ name, description, color });
      toast.success('Saved');
      nav('/groups');
    } finally { setBusy(false); }
  };

  return (
    <>
      <Topbar
        title={editing ? 'Edit Group' : 'New Group'}
        breadcrumb="Audience / Groups"
        description={editing ? 'Update group details.' : 'Create a new contact group.'}
        right={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => nav('/groups')}>Cancel</Button>
            <Button disabled={busy || !name} onClick={submit}>
              {editing ? 'Save changes' : 'Create group'}
            </Button>
          </div>
        }
      />

      <div className="mx-auto w-full max-w-[1000px] px-7 pb-24 pt-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
          {/* One card — a name, a description and a colour don't warrant three. */}
          <Card padded={false}>
            <div className="border-b border-border px-6 py-4">
              <SectionHeader title="Group details" icon={<Users size={16} />} />
            </div>

            <div className="space-y-4 px-6 py-5">
              <FormField label="Name" required>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. VIP Customers" autoFocus />
              </FormField>
              <FormField
                label="Description"
                hintBelow
                hint="Optional — what this group is for. Shown on the group card and in pickers."
              >
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="e.g. Customers who spent over AED 5,000 in the last year"
                />
              </FormField>
            </div>

            <div className="border-t border-border px-6 py-5">
              <div className="mb-3 flex items-baseline justify-between">
                <label className="text-sm font-semibold text-text-primary">Colour</label>
                <span className="text-2xs text-text-muted">
                  {PRESET_COLORS.find((c) => c.hex === color)?.name ?? color}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c.hex}
                    type="button"
                    onClick={() => setColor(c.hex)}
                    title={c.name}
                    aria-label={c.name}
                    aria-pressed={color === c.hex}
                    className={clsx(
                      'flex flex-col items-center gap-1.5 rounded-xl border px-2.5 py-2 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                      color === c.hex
                        ? 'border-primary bg-primary-soft/40'
                        : 'border-border hover:border-border-strong hover:bg-surface-alt',
                    )}
                  >
                    <span className="h-7 w-7 rounded-lg shadow-soft" style={{ background: c.hex }} />
                    <span className={clsx('text-[10px]', color === c.hex ? 'font-semibold text-primary' : 'text-text-muted')}>
                      {c.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </Card>

          {/* Live preview — matches how the group renders in the list. */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="mb-2 text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
              Preview
            </div>
            <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-soft">
              <div className="h-1.5 w-full" style={{ background: color }} />
              <div className="p-5">
                <div className="flex items-start gap-3">
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                    style={{ background: `${color}1f`, color }}
                  >
                    <Users size={19} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-text-primary">
                      {name || 'Group name'}
                    </div>
                    <div className="mt-0.5 line-clamp-2 text-xs text-text-muted">
                      {description || 'No description yet'}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                  <span className="text-xs text-text-muted">Members</span>
                  <span
                    className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                    style={{ background: `${color}1f`, color }}
                  >
                    {editing ? memberCount ?? '—' : 0}
                  </span>
                </div>
              </div>
            </div>
            <p className="mt-3 text-2xs leading-relaxed text-text-muted">
              The colour is used on the group card, in contact chips and on campaign audience
              summaries — pick something you'll recognise at a glance.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
